import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { DashboardSkeleton, FadeSlideIn } from '../../components/ui/SkeletonLoader';
import OnboardingWalkthrough from '../../components/OnboardingWalkthrough';

import ProgressSummary from './components/ProgressSummary';
import DailyReviewCard from './components/DailyReviewCard';
import QuickAccessTiles from './components/QuickAccessTiles';
import RecentActivity from './components/RecentActivity';
import MotivationalSection from './components/MotivationalSection';
import ExtraReviewModal from '../verse-library/components/ExtraReviewModal';
import MemoryHealthCard from './components/MemoryHealthCard';

import { useAuth } from '../../contexts/AuthContext';
import { verseService } from '../../services/supabaseService';
import { avatarService } from '../../services/avatarService';
import { authService } from '../../services/supabaseService';
import { streakService } from '../../services/streakService';
import { activityService } from '../../services/supabaseService';



const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dailyVerse, setDailyVerse] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streakData, setStreakData] = useState(null);
  const [phase0Count, setPhase0Count] = useState(0);
  const [avatarSvg, setAvatarSvg] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [progressStats, setProgressStats] = useState({
    streakDays: 0,
    totalMemorized: 0,
    weeklyProgress: 0,
    weeklyGoal: 7,
    studyTimeToday: 0,
    monthlyGoal: 30,
    completionRate: 0
  });
  const [achievements, setAchievements] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showExtraReviewModal, setShowExtraReviewModal] = useState(false);
  const [allVerses, setAllVerses] = useState([]);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
      loadPhase0Count();
      checkWalkthrough();
    }
  }, [user?.id]);

  const checkWalkthrough = () => {
    if (!user?.id) return;
    // Check if permanently dismissed
    const dismissedUsers = JSON.parse(localStorage.getItem('onboarding_walkthrough_dismissed') || '[]');
    if (dismissedUsers?.includes(user?.id)) return;

    // Check if already shown this session
    const shownThisSession = sessionStorage.getItem('onboarding_walkthrough_shown_this_session');
    if (shownThisSession) return;

    // Mark as shown for this session
    sessionStorage.setItem('onboarding_walkthrough_shown_this_session', '1');
    setShowWalkthrough(true);
  };

  const loadDashboardData = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      
      // Load user profile
      const profile = await authService?.getUserProfile(user?.id);
      if (profile && !profile?.error) {
        setUserProfile(profile);
      }
      
      // Load avatar
      const avatarData = await avatarService?.getUserAvatar(user?.id);
      if (avatarData?.avatar_svg) {
        setAvatarSvg(avatarData?.avatar_svg);
      }

      // Load streak info
      const { data: streak } = await streakService?.getStreakInfo(user?.id);

      // Load memorization stats (total verses, phase breakdown)
      const { data: memStats } = await verseService?.getUserMemorizationStats(user?.id);

      // Load weekly activity — Sunday to today
      const today = new Date();
      const weekStart = new Date(today);
      weekStart?.setDate(today?.getDate() - today?.getDay()); // Sunday start
      weekStart?.setHours(0, 0, 0, 0);

      const { data: weeklyActivities } = await streakService?.getDailyActivity(
        user?.id,
        weekStart?.toISOString()?.split('T')?.[0],
        today?.toISOString()?.split('T')?.[0]
      );

      // Sum weekly reviews completed (not all-time verses practiced)
      const weeklyReviewsDone = weeklyActivities?.reduce(
        (sum, day) => sum + (day?.reviewsCompleted || 0), 0
      ) || 0;

      // Sum total study time this week (in minutes)
      const weeklyStudyMinutes = weeklyActivities?.reduce(
        (sum, day) => sum + (day?.timeSpentMinutes || 0), 0
      ) || 0;

      // Verses memorized = phases 4+ (Familiar, Known, Mastered, Expert, Memorized)
      const versesMemorized = (memStats?.phase4Count || 0) +
        (memStats?.phase5Count || 0) +
        (memStats?.phase6Count || 0) +
        (memStats?.phase7Count || 0);

      // Weekly goal = 7 reviews per week (1 per day)
      const weeklyGoal = 7;

      setProgressStats({
        streakDays: streak?.currentStreak || 0,
        totalMemorized: versesMemorized,
        weeklyProgress: weeklyReviewsDone,
        weeklyGoal: weeklyGoal,
        studyTimeWeek: weeklyStudyMinutes,
        monthlyGoal: 30,
        completionRate: memStats?.successRate || 0
      });

      setStreakData(streak);

      // Load recent activities
      const { data: activities } = await activityService?.getUserActivities(user?.id, 10);
      if (activities) {
        const mapped = activities?.map(a => ({
          id: a?.id,
          type: a?.activity_type,
          title: a?.title,
          description: a?.description,
          verse: a?.verses?.reference || null,
          timestamp: a?.created_at
        }));
        setRecentActivities(mapped);
      }

    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPhase0Count = async () => {
    if (!user?.id) return;
    
    try {
      const { data: verses, error } = await verseService?.getUserVerses(user?.id);
      
      if (!error && verses) {
        const phase0 = verses?.filter(v => v?.currentPhase === 0 || v?.current_phase === 0);
        setPhase0Count(phase0?.length || 0);
        setAllVerses(verses || []);
      }
    } catch (err) {
      console.error('Error loading Phase 0 count:', err);
    }
  };

  const handleStartPhase0Practice = async () => {
    try {
      // Fetch Phase 0 verses
      const { data: verses, error } = await verseService?.getUserVerses(user?.id);
      
      if (!error && verses) {
        const phase0Verses = verses?.filter(v => v?.currentPhase === 0 || v?.current_phase === 0);
        
        if (phase0Verses?.length > 0) {
          // Navigate to memorization-practice with Phase 0 verses and autoplay enabled
          navigate('/memorization-practice', {
            state: {
              phase0Verses: phase0Verses,
              isPhase0Session: true
            }
          });
        }
      }
    } catch (err) {
      console.error('Error loading Phase 0 verses:', err);
    }
  };

  const handleTileClick = (tile) => {
    console.log('Navigating to:', tile?.title);
    // Additional analytics or state updates can be handled here
  };

  const handleExtraReview = () => {
    setShowExtraReviewModal(true);
  };

  const handleStartExtraReview = ({ verses: selectedVerses, rescheduleOnResults, updateLastReviewed }) => {
    navigate('/daily-review-system', {
      state: {
        extraReviewVerses: selectedVerses,
        rescheduleOnResults,
        updateLastReviewed: updateLastReviewed !== false,
        isExtraReview: true,
      }
    });
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        {showWalkthrough && <OnboardingWalkthrough onComplete={() => setShowWalkthrough(false)} />}
        <main>
          <DashboardSkeleton />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {showWalkthrough && <OnboardingWalkthrough onComplete={() => setShowWalkthrough(false)} />}
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24 md:pb-6">
        <FadeSlideIn className="space-y-4 sm:space-y-6">
          {/* User Avatar + Welcome — inline row */}
          <div className="flex items-center gap-4 mb-6 sm:mb-8">
            {/* Avatar */}
            <Link to="/user-profile" className="relative group cursor-pointer shrink-0">
              {/* Amber glow border matching theme */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary via-amber-400 to-yellow-500 rounded-full blur-[3px] opacity-70 group-hover:opacity-100 group-hover:blur-[5px] transition-all duration-300"></div>
              <div className="relative bg-card rounded-full p-[3px] shadow-lg ring-2 ring-primary/40">
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden bg-gradient-to-br from-primary/20 to-amber-500/20 flex items-center justify-center"
                >
                  {avatarSvg ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: avatarSvg }}
                      className="w-full h-full flex items-center justify-center"
                      style={{ transform: 'scale(1.1)' }}
                    />
                  ) : (
                    <div className="text-xl sm:text-2xl font-bold text-primary">
                      {userProfile?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
              </div>
            </Link>

            {/* Welcome text */}
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold text-foreground truncate">
                Welcome back{userProfile?.full_name ? `, ${userProfile?.full_name?.split(' ')?.[0]}` : ''}!
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                {new Date()?.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8">
              {/* Quick Access Tiles */}
              <QuickAccessTiles onTileClick={handleTileClick} />

              {/* Daily Review Section */}
              <DailyReviewCard onExtraReview={handleExtraReview} />

              {/* Progress Section */}
              <ProgressSummary
                stats={progressStats ?? { streakDays: 0, totalMemorized: 0, weeklyProgress: 0, weeklyGoal: 7, studyTimeToday: 0, monthlyGoal: 30, completionRate: 0 }}
                achievements={achievements ?? []}
              />

              {/* Phase 0 Practice Card */}
              {phase0Count > 0 && (
                <div className="group bg-gradient-to-br from-primary via-primary to-primary/80 rounded-lg shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-300 ease-out p-4 sm:p-6 text-primary-foreground border border-primary/20">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary-foreground/20 p-2.5 sm:p-3 rounded-lg shrink-0 group-hover:scale-110 group-hover:bg-primary-foreground/30 transition-all duration-300">
                        <Play className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold">Phase 0 Practice</h3>
                        <p className="text-primary-foreground/70 text-xs sm:text-sm">Sequential autoplay for hands-free learning</p>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-2xl sm:text-3xl font-bold">{phase0Count}</div>
                      <div className="text-primary-foreground/70 text-xs sm:text-sm">verses ready</div>
                    </div>
                  </div>
                  <p className="text-primary-foreground/80 text-xs sm:text-sm mb-4">
                    Practice your Phase 0 verses with progressive chunking. Perfect for memorizing while driving or relaxing.
                  </p>
                  <Button
                    fullWidth
                    onClick={handleStartPhase0Practice}
                    className="bg-card hover:bg-card/90 text-foreground text-sm min-h-[44px] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200"
                    iconName="Play"
                    iconPosition="left"
                  >
                    Memorize Phase 0 Verses!
                  </Button>
                </div>
              )}
            </div>

            {/* Right Column - Secondary Content */}
            <div className="space-y-4 sm:space-y-6">
              <MemoryHealthCard onExtraReview={handleExtraReview} />
              <RecentActivity activities={recentActivities} />
              <MotivationalSection />
            </div>
          </div>
        </FadeSlideIn>
      </main>
      {/* Extra Review Modal */}
      <ExtraReviewModal
        isOpen={showExtraReviewModal}
        onClose={() => setShowExtraReviewModal(false)}
        verses={allVerses}
        onStartReview={handleStartExtraReview}
      />
    </div>
  );
};

export default Dashboard;